/**
 * SVG layer above the cell grid. Renders the cascade tree
 * (one parent per impacted node, animated marching dots) +
 * the incident origin marker.
 *
 * Cell-centre rects come from a ResizeObserver-watched bag of
 * (capability_id -> {x, y}) measurements computed by the parent
 * page shell on resize / layout change. Re-positions cleanly
 * on viewport changes.
 */
import type { TreeEdge } from "twin/capmap/cascadeTree";

export interface CellRect {
  x: number;
  y: number;
}

export interface CascadeOverlayProps {
  /** capability_id -> centre coords relative to the overlay box */
  rects: Map<string, CellRect>;
  width: number;
  height: number;
  treeEdges: TreeEdge[];
  originId: string | null;
  litSet: Set<string> | null;
  selectedId: string | null;
}

export function CascadeOverlay({
  rects,
  width,
  height,
  treeEdges,
  originId,
  litSet,
  selectedId,
}: CascadeOverlayProps): JSX.Element {
  return (
    <svg
      data-testid="capmap-cascade-overlay"
      className="capmap-overlay"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      pointerEvents="none"
    >
      {treeEdges.map((edge, i) => {
        const a = rects.get(edge.upstreamId);
        const b = rects.get(edge.dependentId);
        if (!a || !b) return null;
        const lit =
          litSet !== null &&
          litSet.has(edge.dependentId) &&
          (litSet.has(edge.upstreamId) || edge.upstreamId === selectedId);
        const dim = litSet !== null && !lit;
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2 - 14;
        const d = `M ${a.x} ${a.y} Q ${mx} ${my}, ${b.x} ${b.y}`;
        return (
          <g
            key={i}
            data-testid="capmap-cascade-edge"
            data-upstream-id={edge.upstreamId}
            data-dependent-id={edge.dependentId}
            data-lit={lit ? "true" : "false"}
            style={{ opacity: dim ? 0.12 : 1 }}
          >
            <path
              className={`capmap-flow ${lit ? "capmap-flow-lit" : "capmap-flow-line"}`}
              d={d}
              fill="none"
            />
          </g>
        );
      })}
      {originId && rects.get(originId) && (
        <g data-testid="capmap-cascade-origin">
          <circle
            className="capmap-flow-orig-ring"
            cx={rects.get(originId)!.x}
            cy={rects.get(originId)!.y}
            r="14"
            fill="none"
          />
          <circle
            className="capmap-flow-orig"
            cx={rects.get(originId)!.x}
            cy={rects.get(originId)!.y}
            r="3.5"
          />
        </g>
      )}
    </svg>
  );
}
